import React, { useState } from 'react';
import { UtilService } from '../../../../../core/services/util.service';
import { User } from '../../../../public/models/user.model';
import { useNavigate } from 'react-router-dom';
import { AuthService } from '../../../../public/services/auth-login.service';
import {
  SaveOutlined,
  LockOutlined,
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  EyeOutlined,
  EyeInvisibleOutlined,
} from '@ant-design/icons';
import { useProfile } from './ProfileContext';
import { Button, Input } from 'antd';
import { route } from '../../../../../constants/routes';
import { jo } from '../../../../../theme/css-vars';

const ChangePassword: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User>(new User());
  const { profile, companyId } = useProfile();
  const [confirmPassword, setConfirmPassword] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  const passwordValidation = [
    {
      rule: user.password?.length >= 8,
      message: 'At least 8 characters',
    },
    {
      rule: /[A-Z]/.test(user.password || ''),
      message: 'At least 1 uppercase letter',
    },
    {
      rule: /[a-z]/.test(user.password || ''),
      message: 'At least 1 lowercase letter',
    },
    {
      rule: /[0-9]/.test(user.password || ''),
      message: 'At least 1 number',
    },
    {
      rule: /[!@#$%^&*(),.?":{}|<>]/.test(user.password || ''),
      message: 'At least 1 special character (!@#$...)',
    },
    {
      rule: user.password && profile?.email && user.password !== profile.email,
      message: 'Cannot be the same as email',
    },
  ];

  const Validate = (user: User, callback: () => void) => {
    setHasSubmitted(true);
    const errors: string[] = [];

    if (!user.password) {
      errors.push('You must enter the new password.');
    }

    if (!confirmPassword) {
      errors.push('You must confirm your password.');
    }

    if (!isPasswordValid) {
      errors.push('Password does not meet the requirements.');
    }

    if (!isConfirmPasswordValid) {
      errors.push('Passwords do not match.');
    }

    if (errors.length > 0) {
      UtilService.Alert('Review password', 'info', errors.join(' </br> '));
      return;
    }

    callback();
  };

  const HandleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setUser((prevUser) => ({ ...prevUser, [name]: value }));
  };

  const ChangePassword = async () => {
    if (!profile?.id) {
      UtilService.Alert(
        'Password not changed',
        'error',
        'User profile not found'
      );
      return;
    }
    if (!companyId) {
      UtilService.Alert(
        'Password not changed',
        'error',
        'Company ID not found'
      );
      return;
    }
    try {
      setSaving(true);
      await AuthService.ChangePassword(
        companyId,
        profile.id,
        user.password || ''
      );
      UtilService.Alert(
        'Password changed',
        'success',
        'Your account is secured with the new password.'
      );
      navigate(route('/dashboard'));
    } catch (error) {
      console.error('Error changing password:', error);
      UtilService.Alert('Password not changed', 'error', 'Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const isPasswordValid = passwordValidation.every((item) => item.rule);
  const isConfirmPasswordValid =
    user.password === confirmPassword && confirmPassword !== '';
  const showValidation = hasSubmitted || Boolean(user.password);

  return (
    <section className="security-workspace" aria-labelledby="security-title">
      <div className="security-workspace__intro">
        <span className="profile-card__icon">
          <LockOutlined />
        </span>
        <div>
          <span>Account security</span>
          <h2 id="security-title">Change password</h2>
          <p>Choose a unique password you do not use for another service.</p>
        </div>
      </div>
      <div className="security-workspace__content">
        <div className="security-workspace__fields">
          <div>
            <label htmlFor="password" className="security-workspace__label">
              New Password
            </label>
            <Input.Password
              id="password"
              placeholder="Enter your password..."
              name="password"
              value={user.password}
              onChange={HandleInput}
              iconRender={(visible) =>
                visible ? (
                  <EyeOutlined style={{ color: jo.primary }} />
                ) : (
                  <EyeInvisibleOutlined style={{ color: jo.textDisabled }} />
                )
              }
            />
          </div>
          <div>
            <label
              htmlFor="confirmPassword"
              className="security-workspace__label"
            >
              Confirm Password
            </label>
            <Input.Password
              id="confirmPassword"
              placeholder="Confirm your password..."
              name="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              status={
                confirmPassword && !isConfirmPasswordValid ? 'error' : undefined
              }
              iconRender={(visible) =>
                visible ? (
                  <EyeOutlined style={{ color: jo.primary }} />
                ) : (
                  <EyeInvisibleOutlined style={{ color: jo.textDisabled }} />
                )
              }
            />
          </div>
        </div>
        <aside className="security-requirements">
          <h3>Password requirements</h3>
          <p>All requirements must be met before saving.</p>
          {passwordValidation.map((item, index) => {
            let icon;

            if (!showValidation) {
              icon = <span className="security-requirements__pending" />;
            } else if (item.rule) {
              icon = <CheckCircleTwoTone twoToneColor={jo.success} />;
            } else {
              icon = <CloseCircleTwoTone twoToneColor={jo.danger} />;
            }

            return (
              <div
                key={index}
                className={
                  showValidation ? (item.rule ? 'is-valid' : 'is-invalid') : ''
                }
              >
                {icon}
                <span>{item.message}</span>
              </div>
            );
          })}
        </aside>
      </div>
      <footer className="security-workspace__actions">
        <span>Your session remains active after the password is updated.</span>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={saving}
          onClick={() => Validate(user, ChangePassword)}
          disabled={!isPasswordValid || !isConfirmPasswordValid}
        >
          Update password
        </Button>
      </footer>
    </section>
  );
};

export default ChangePassword;
